import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption, type ActionMenuItem } from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useNavTarget } from 'contexts/NavigationContext';
import { purchaseDebitNoteService, partyService, type PartySetoffService } from 'api/services';
import type { SetoffDto, SetoffQueryParams, SetoffCandidatesDto, SetoffCandidateDto, PaginationMeta } from 'api/types';
import type { ScreenCode } from 'auth/screenCodes';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

// ADR-0051: Set-off — match a party's open DEBIT items against its open CREDIT items on one
// ledger without moving money. Each item settles at its own booked rate; a booked-rate difference
// posts to FX Gain/Loss. Items in ONE currency match in that currency; items in SEVERAL currencies
// match in home currency at the set-off date's agreed rate per currency (§4 revision). No edit —
// cancel reopens every item.
//
// ADR-0060: one screen for both ledgers. Supplier Set-off (AP) matches debit balances against
// bills; Customer Set-off (AR) matches credits against invoices and short-paid items. "Mark
// short-paid" / "Mark withheld" closes disputed bills with no money moving: each amount stays open
// on the same ledger as a deduction item with its reason.

const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const KIND_LABEL: Record<string, string> = { BILL: 'Bill', ON_ACCOUNT: 'On account', ADVANCE: 'Advance' };
const CLASS_LABEL: Record<string, string> = {
  PURCHASE_INVOICE: 'Bill', PURCHASE_DEBIT_NOTE: 'Debit note', PAYMENT: 'Payment', JOURNAL: 'Journal', OPENING_BALANCE: 'Opening',
  SALES_INVOICE: 'Invoice', CREDIT_NOTE: 'Credit note', RECEIPT: 'Receipt',
};
const bookedRate = (i: SetoffCandidateDto) => (i.fcBalance ? i.baseBalance / i.fcBalance : 0);
/** Grid columns that are never hidden (ENTERPRISE_SCREEN_CHECKLIST §4.1). */
const STRUCTURAL_COLUMN_IDS = ['actions'];

/** Everything that differs between the supplier (AP) and customer (AR) faces of the set-off. */
export interface PartySetoffConfig {
  role: 'SUPPLIER' | 'CUSTOMER';
  screenCode: ScreenCode;
  title: string;
  description: string;
  service: PartySetoffService;
  useList: (params?: SetoffQueryParams) => { data: SetoffDto[]; loading: boolean; pagination: PaginationMeta; refetch: () => void };
  /** Party master filter for the picker. */
  partyType: 'SUPPLIER' | 'CUSTOMER';
  partyWord: string;        // Supplier / Customer
  /** The side the party's balances sit on (shown first): AP debit balances, AR credit balances. */
  balanceSide: 'DR' | 'CR';
  balanceTitle: string;
  balanceHint: string;
  openTitle: string;
  openHint: string;
  /** ADR-0060 wording: withheld / short-paid. */
  deductionWord: string;
  markLabel: string;        // Mark withheld / Mark short-paid
  markHint: string;
}

const itemLabel = (c: SetoffCandidateDto) =>
  `${CLASS_LABEL[c.sourceVoucherClass || ''] || c.sourceVoucherClass || KIND_LABEL[c.itemKind] || c.itemKind} ${c.sourceTransactionNo || ''}`.trim();
/** A deduction item (ADR-0060) says what it is: kind, source bill, reason, remarks. */
const deductionNote = (c: SetoffCandidateDto) => !c.deductionKind ? '' :
  [c.deductionKind === 'WITHHELD' ? 'Withheld' : 'Short-paid', c.sourceReferenceNo ? `on ${c.sourceReferenceNo}` : '', c.reasonLabel || c.reasonCode || '', c.remarks || '']
    .filter(Boolean).join(' · ');
const DEDUCTION_ROW: React.CSSProperties = { background: 'var(--warning-bg, #fef3c7)' };
const DEDUCTION_TEXT: React.CSSProperties = { fontSize: '0.7rem', color: 'var(--warning-text, #92400e)' };

/**
 * Default party picker over the party master, narrowed to the role unless the user widens it.
 * Labels go into the caller's cache so a picked party can travel to the other New Set-off mode.
 */
const usePartyLoader = (partyType: string, searchAll: boolean, labels: React.MutableRefObject<Map<string, string>>) => useCallback(async (input: string): Promise<SelectOption[]> => {
  try {
    const r = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined, partyType: searchAll ? undefined : partyType });
    return r.data.map(p => {
      const label = `${p.partyCode} - ${p.partyName}`;
      labels.current.set(p.id, label);
      return { value: p.id, label };
    });
  } catch { return []; }
}, [partyType, searchAll, labels]);

/** The two jobs of New Set-off (ADR-0060): match items, or mark disputed bills short-paid / withheld. */
export type NewSetoffMode = 'MATCH' | 'MARK';
export interface CarriedParty { id: string; label: string }

/** Header switch between the two modes of New Set-off. */
const ModeSwitch: React.FC<{ config: PartySetoffConfig; mode: NewSetoffMode; onSwitch: (m: NewSetoffMode) => void; disabled?: boolean }> = ({ config, mode, onSwitch, disabled }) => {
  const btn = (m: NewSetoffMode, label: string, title: string) => (
    <button type="button" disabled={disabled} title={title} aria-pressed={mode === m}
      onClick={() => { if (mode !== m) onSwitch(m); }}
      style={{
        border: 'none', cursor: disabled ? 'default' : 'pointer', padding: '0.3rem 0.75rem', fontSize: '0.78rem', fontWeight: 600,
        background: mode === m ? 'var(--primary, #2563eb)' : 'transparent', color: mode === m ? '#fff' : 'var(--text-secondary)',
      }}>{label}</button>
  );
  return (
    <div role="group" aria-label="What to do" style={{ display: 'inline-flex', border: '1px solid var(--form-border-color)', borderRadius: '0.4rem', overflow: 'hidden' }}>
      {btn('MATCH', 'Match items', `Match the ${config.partyWord.toLowerCase()}'s ${config.balanceTitle.toLowerCase()} against its ${config.openTitle.toLowerCase()}`)}
      {btn('MARK', config.markLabel, config.markHint)}
    </div>
  );
};

// ---- New set-off modal ----
interface SetoffFormProps {
  config: PartySetoffConfig;
  isOpen: boolean;
  /** Pre-pick (supplier only): a debit note whose on-account balance should be set off. */
  fromDebitNoteId?: string | null;
  /** The party already picked in the other mode — carried across the switch. */
  initialParty?: CarriedParty | null;
  onPartyChange?: (party: CarriedParty | null) => void;
  /** The Match / Mark switch shown in the header. */
  modeSwitch?: React.ReactNode;
  onSaved: () => void;
  onClose: () => void;
}
const SetoffFormModal: React.FC<SetoffFormProps> = ({ config, isOpen, fromDebitNoteId, initialParty, onPartyChange, modeSwitch, onSaved, onClose }) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  /** Default posting date: today when it falls in the selected financial year, else the year's last (or first) day. */
  const docToday = () => defaultDateIn(fyRef.current);
  const { service } = config;
  const [partyId, setPartyId] = useState('');
  const [partyLabel, setPartyLabel] = useState('');
  const [searchAll, setSearchAll] = useState(false);
  const [cands, setCands] = useState<SetoffCandidatesDto | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  /** Set-off date rate to home currency per foreign currency (only used when several currencies are picked). */
  const [rates, setRates] = useState<Record<string, string>>({});
  const [setoffDate, setSetoffDate] = useState(today());
  const [narration, setNarration] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadCandidates = useCallback(async (pid: string, prePickTxnId?: string | null) => {
    setCands(null); setAmounts({}); setRates({});
    if (!pid) return;
    setLoading(true);
    try {
      const c = await service.candidates(pid);
      setCands(c);
      // Rate defaults: the most recently booked item's rate per foreign currency (no rate master
      // yet) — the user replaces it with the rate agreed for the set-off date.
      const latest: Record<string, SetoffCandidateDto> = {};
      [...c.debitItems, ...c.creditItems].forEach(i => {
        const ccy = i.currencyCode || c.functionalCurrencyCode || '';
        if (ccy === (c.functionalCurrencyCode || '')) return;
        if (!latest[ccy] || (i.itemDate || '') > (latest[ccy].itemDate || '')) latest[ccy] = i;
      });
      setRates(Object.fromEntries(Object.entries(latest).map(([ccy, i]) => [ccy, String(Number(bookedRate(i).toFixed(6)))])));
      const pre = prePickTxnId ? c.debitItems.find(i => i.sourceTransactionId === prePickTxnId) : undefined;
      if (pre) setAmounts({ [pre.openItemId]: String(pre.fcBalance) });
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || `Could not load the ${config.partyWord.toLowerCase()}'s open items.`);
    } finally { setLoading(false); }
  }, [service, config.partyWord]);

  useEffect(() => {
    if (!isOpen) return;
    setPartyId(''); setPartyLabel(''); setSearchAll(false); setCands(null); setAmounts({}); setRates({});
    setSetoffDate(docToday()); setNarration('');
    if (fromDebitNoteId && config.role === 'SUPPLIER') {
      purchaseDebitNoteService.getById(fromDebitNoteId).then(dn => {
        setPartyId(dn.partyId); setPartyLabel(dn.partyName || '');
        loadCandidates(dn.partyId, dn.transactionId);
      }).catch(() => notificationService.toast.error('Could not load the debit note.'));
    } else if (initialParty?.id) {
      setPartyId(initialParty.id); setPartyLabel(initialParty.label);
      loadCandidates(initialParty.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fromDebitNoteId, loadCandidates]);

  const partyLabels = useRef(new Map<string, string>());
  const loadParties = usePartyLoader(config.partyType, searchAll, partyLabels);

  const hc = cands?.functionalCurrencyCode || '';
  const ccyOf = (i: SetoffCandidateDto) => i.currencyCode || hc;
  const debitRows = cands?.debitItems || [];
  const creditRows = cands?.creditItems || [];
  // The party's balances (shown first) and the items they clear — AP: debit balances vs bills;
  // AR: credits vs invoices and short-paid items.
  const balanceRows = config.balanceSide === 'DR' ? debitRows : creditRows;
  const openRows = config.balanceSide === 'DR' ? creditRows : debitRows;
  const amt = (id: string) => parseFloat(amounts[id] || '') || 0;
  const picked = [...debitRows, ...creditRows].filter(i => amt(i.openItemId) > 0);
  const pickedCurrencies = Array.from(new Set(picked.map(ccyOf)));
  const multi = pickedCurrencies.length > 1;
  const foreignPicked = pickedCurrencies.filter(c => c !== hc);
  const anyForeign = foreignPicked.length > 0;
  const setoffRateOf = (ccy: string) => (ccy === hc ? 1 : parseFloat(rates[ccy] || '') || 0);
  /** What an item's amount counts toward its side: the amount itself (one currency) or its HC value at the set-off rate (several). */
  const matchValue = (i: SetoffCandidateDto, a: number) => (multi ? a * setoffRateOf(ccyOf(i)) : a);
  const matchCcy = multi ? hc : (pickedCurrencies[0] || '');
  const usedOf = (rows: SetoffCandidateDto[]) => round2(rows.reduce((s, i) => s + matchValue(i, amt(i.openItemId)), 0));
  const balanceUsed = usedOf(balanceRows);
  const openUsed = usedOf(openRows);
  // Each 2-dp amount carries ±0.005 of rounding, worth ±0.005 × its rate in home currency.
  const tolerance = multi ? picked.reduce((s, i) => s + 0.005 * setoffRateOf(ccyOf(i)), 0) + 0.005 : 0.005;

  // Preview of the server's leg math (each item at its own BOOKED rate; FX = D − C, where DR legs
  // consume credit-side items and CR legs consume debit-side items).
  const legBase = (i: SetoffCandidateDto) => {
    const a = amt(i.openItemId);
    if (a <= 0) return 0;
    return Math.abs(a - i.fcBalance) < 0.005 ? i.baseBalance : round2(a * bookedRate(i));
  };
  const baseOf = (rows: SetoffCandidateDto[]) => round2(rows.reduce((s, i) => s + legBase(i), 0));
  const drLegBase = baseOf(creditRows);
  const crLegBase = baseOf(debitRows);
  const sidesMatch = balanceUsed > 0 && openUsed > 0 && Math.abs(balanceUsed - openUsed) <= tolerance;
  // The exchange difference only exists once the sides match; on unequal sides nothing posts.
  const fx = sidesMatch ? round2(drLegBase - crLegBase) : 0;

  const pickedUnits = new Set(picked.map(i => i.businessUnitId || ''));
  const latestDate = picked.map(i => (i.itemDate || '').slice(0, 10)).sort().pop() || '';
  const balanceWord = config.balanceTitle.toLowerCase();
  const openWord = config.openTitle.toLowerCase();

  const problems: string[] = [];
  [...debitRows, ...creditRows].forEach(i => {
    const a = amt(i.openItemId);
    if (a < 0) problems.push(`${itemLabel(i)}: the amount cannot be negative.`);
    if (a > i.fcBalance + 0.005) problems.push(`${itemLabel(i)}: more than its open ${ccyOf(i)} ${money(i.fcBalance)}.`);
  });
  if (multi) foreignPicked.forEach(c => { if (!(setoffRateOf(c) > 0)) problems.push(`State the set-off rate for ${c} (1 ${c} in ${hc}).`); });
  if (balanceUsed > 0 && openUsed > 0 && !sidesMatch)
    problems.push(multi
      ? `The sides must match in ${hc} at the set-off rates: ${balanceWord} ${hc} ${money(balanceUsed)} vs ${openWord} ${hc} ${money(openUsed)}.`
      : `The sides must match: ${balanceWord} ${money(balanceUsed)} vs ${openWord} ${money(openUsed)}.`);
  if (cands && !cands.balancedBuPosting && pickedUnits.size > 1) problems.push('The picked items are in different business units — set off one unit at a time (balanced-BU posting is off).');
  if (latestDate && setoffDate < latestDate) problems.push(`The date cannot be before the latest picked item (${latestDate}).`);
  const canPost = balanceUsed > 0 && openUsed > 0 && problems.length === 0 && !!setoffDate;

  const setAmount = (id: string, v: string) => setAmounts(prev => ({ ...prev, [id]: v }));
  /** Convert a match-unit value into an item's own currency (identity unless several currencies). */
  const toItemAmount = (i: SetoffCandidateDto, value: number, assumeMulti: boolean) => {
    const r = setoffRateOf(ccyOf(i));
    return assumeMulti ? (r > 0 ? value / r : 0) : value;
  };
  /**
   * "All": once the OTHER side carries amounts, fill only what is still needed to match it
   * (capped at this item's open balance); across currencies the need is converted at the set-off
   * rates. With nothing on the other side yet, take the whole open balance.
   */
  const fillAll = (i: SetoffCandidateDto, rowsOfSide: SetoffCandidateDto[], otherRows: SetoffCandidateDto[]) => {
    const otherPicked = otherRows.filter(x => amt(x.openItemId) > 0);
    if (otherPicked.length === 0) { setAmount(i.openItemId, String(i.fcBalance)); return; }
    // Would this item make the set-off multi-currency? Then the match is in home currency.
    const ccys = new Set([...picked.filter(x => x.openItemId !== i.openItemId).map(ccyOf), ccyOf(i)]);
    const willBeMulti = ccys.size > 1;
    const val = (x: SetoffCandidateDto, a: number) => (willBeMulti ? a * setoffRateOf(ccyOf(x)) : a);
    const otherUsed = otherRows.reduce((s, x) => s + val(x, amt(x.openItemId)), 0);
    const thisSideOthers = rowsOfSide.filter(x => x.openItemId !== i.openItemId).reduce((s, x) => s + val(x, amt(x.openItemId)), 0);
    const needed = Math.max(0, otherUsed - thisSideOthers);
    const take = round2(Math.min(i.fcBalance, toItemAmount(i, needed, willBeMulti)));
    setAmount(i.openItemId, take > 0 ? String(take) : '');
  };
  /** Oldest first, within the picked currencies (or all when nothing is picked yet). Balance amounts already typed set the target; otherwise the smaller side's total. */
  const autoMatch = () => {
    const typedBalance = balanceRows.some(i => amt(i.openItemId) > 0);
    const ccys = new Set([...debitRows, ...creditRows].map(ccyOf));
    const willBeMulti = typedBalance ? multi || new Set([...picked.map(ccyOf), ...openRows.map(ccyOf)]).size > 1 : ccys.size > 1;
    if (willBeMulti && [...ccys].some(c => c !== hc && !(setoffRateOf(c) > 0))) {
      notificationService.toast.warning('State the set-off rate for each currency first.'); return;
    }
    const val = (i: SetoffCandidateDto, a: number) => (willBeMulti ? a * setoffRateOf(ccyOf(i)) : a);
    const target = typedBalance
      ? balanceRows.reduce((s, i) => s + val(i, amt(i.openItemId)), 0)
      : Math.min(balanceRows.reduce((s, i) => s + val(i, i.fcBalance), 0), openRows.reduce((s, i) => s + val(i, i.fcBalance), 0));
    const fill = (rows: SetoffCandidateDto[], total: number, next: Record<string, string>) => {
      let left = total;
      [...rows].sort((a, b) => (a.itemDate || '').localeCompare(b.itemDate || '')).forEach(i => {
        const take = round2(Math.min(i.fcBalance, toItemAmount(i, left, willBeMulti)));
        next[i.openItemId] = take > 0 ? String(take) : '';
        left = Math.max(0, left - val(i, take));
      });
    };
    const next: Record<string, string> = { ...amounts };
    if (!typedBalance) fill(balanceRows, target, next);
    fill(openRows, target, next);
    setAmounts(next);
  };

  const submit = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const items = picked.map(i => ({ openItemId: i.openItemId, amount: amt(i.openItemId) }));
      const saved = await service.create({
        partyId, setoffDate, narration: narration.trim() || undefined, items,
        rates: multi ? foreignPicked.map(c => ({ currencyCode: c, rate: setoffRateOf(c) })) : undefined,
      });
      notificationService.toast.success(`Set-off ${saved.setoffNumber || ''} posted — ${saved.currencyCode} ${money(saved.totalAmount)} matched${saved.fxAmount ? `, FX ${saved.fxAmount > 0 ? 'gain' : 'loss'} ${money(Math.abs(saved.fxAmount))}` : ''}.`);
      onSaved(); onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not post the set-off.');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  const itemTable = (title: string, hint: string, rows: SetoffCandidateDto[], otherRows: SetoffCandidateDto[], used: number) => (
    <div style={{ flex: 1, minWidth: 360 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem' }}>
        <span className="form-label" style={{ margin: 0 }}>{title}</span>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Using <strong>{matchCcy} {money(used)}</strong>{anyForeign && used > 0 && !multi && <span style={{ color: 'var(--text-muted)' }}> · {hc} {money(baseOf(rows))}</span>}</span>
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{hint}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
          <th style={{ padding: '0.3rem' }}>Item</th><th style={{ padding: '0.3rem' }}>Date</th>
          <th style={{ padding: '0.3rem', textAlign: 'right' }}>Open</th><th style={{ padding: '0.3rem', width: 140, textAlign: 'right' }}>Use</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>None open.</td></tr>}
          {rows.map(i => {
            const ccy = ccyOf(i);
            const foreign = ccy !== hc;
            const a = amt(i.openItemId);
            const note = deductionNote(i);
            return (
              <tr key={i.openItemId} style={{ borderTop: '1px solid var(--border-color)', ...(note ? DEDUCTION_ROW : {}) }}>
                <td style={{ padding: '0.3rem' }}>
                  <div>{note ? `${i.deductionKind === 'WITHHELD' ? 'Withheld' : 'Short-paid'} · ${i.sourceReferenceNo || i.referenceNo || ''}` : itemLabel(i)}</div>
                  {note
                    ? <div style={DEDUCTION_TEXT}>{[i.reasonLabel || i.reasonCode, i.remarks, i.sourceTransactionNo ? `from ${i.sourceTransactionNo}` : ''].filter(Boolean).join(' · ')}</div>
                    : <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{i.referenceNo || ''}{i.businessUnitName ? ` · ${i.businessUnitName}` : ''}</div>}
                </td>
                <td style={{ padding: '0.3rem' }}>{(i.itemDate || '').slice(0, 10)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                  {ccy} {money(i.fcBalance)}
                  {foreign && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{hc} {money(i.baseBalance)} @ {bookedRate(i).toFixed(4)} booked</div>}
                </td>
                <td style={{ padding: '0.3rem' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                    <input className="form-input" type="number" style={{ textAlign: 'right', fontSize: '0.8rem' }} placeholder="0.00"
                      value={amounts[i.openItemId] ?? ''} onChange={e => setAmount(i.openItemId, e.target.value)}
                      aria-label={`Use from ${itemLabel(i)}`} />
                    <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '0.15rem 0.35rem' }}
                      title="Use the whole open balance — or, once the other side has amounts, just what is needed to match it"
                      onClick={() => fillAll(i, rows, otherRows)}>All</button>
                  </div>
                  {a > 0 && (foreign || multi) && (
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                      {multi && foreign && <>= {hc} {money(a * setoffRateOf(ccy))} at set-off rate · </>}clears {hc} {money(legBase(i))} booked
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {(saving || loading) && <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}><div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}><h2 className="modal-title">New {config.title}</h2>{modeSwitch}</div>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-grid-3">
            <div className="form-group">
              <SyutSelect key={String(searchAll)} label={<>{config.partyWord} <span className="text-danger">*</span></>} isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={partyId ? [{ value: partyId, label: partyLabel }] : undefined}
                value={partyId} onChange={v => {
                  const id = (v as string) || '';
                  const label = partyLabels.current.get(id) || '';
                  setPartyId(id); setPartyLabel(label); loadCandidates(id);
                  onPartyChange?.(id ? { id, label } : null);
                }}
                menuPortalTarget={document.body} placeholder={searchAll ? 'Search all parties' : `Select ${config.partyWord.toLowerCase()}`} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={searchAll} onChange={e => setSearchAll(e.target.checked)} /> {config.partyWord} not listed? Search all parties
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Set-off Date <span className="text-danger">*</span></label>
              <FyDateInput className="form-input" value={setoffDate} onChange={setSetoffDate} />
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Its own date — on or after the latest picked item</div>
            </div>
          </div>

          {multi && (
            <div style={{ margin: '0.25rem 0 0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', fontSize: '0.8rem' }}>
              <div style={{ marginBottom: '0.35rem' }}>
                <strong>Several currencies</strong> — the two sides match in {hc} at the rate agreed for the set-off date
                (usually the one on the {config.partyWord.toLowerCase()}'s statement). Each item still clears at its own booked rate; any difference is exchange gain or loss.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {foreignPicked.map(c => (
                  <label key={c} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    1 {c} =
                    <input className="form-input" type="number" step="0.000001" min="0" style={{ width: 120, textAlign: 'right', fontSize: '0.8rem' }}
                      value={rates[c] ?? ''} onChange={e => setRates(prev => ({ ...prev, [c]: e.target.value }))} />
                    {hc}
                  </label>
                ))}
              </div>
            </div>
          )}

          {cands && (debitRows.length + creditRows.length > 0) ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0.25rem 0 0.5rem' }}>
                <button className="btn btn-secondary" onClick={autoMatch} style={{ fontSize: '0.75rem' }} title={`Fill oldest first: typed ${balanceWord} set the target, otherwise the smaller side's total`}>Auto-match (oldest first)</button>
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                {itemTable(config.balanceTitle, config.balanceHint, balanceRows, openRows, balanceUsed)}
                {itemTable(config.openTitle, config.openHint, openRows, balanceRows, openUsed)}
              </div>
              {problems.length > 0 && (
                <div style={{ fontSize: '0.8rem', marginTop: '0.6rem', padding: '0.45rem 0.7rem', borderRadius: 6, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.35)', color: '#dc2626' }}>
                  {problems.map((p, i) => <div key={i}>⚠ {p}</div>)}
                </div>
              )}
            </>
          ) : cands ? (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>This {config.partyWord.toLowerCase()} has no open items to set off.</div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Narration</label>
              <textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} placeholder="Defaults to the matched references" />
            </div>
            <div style={{ minWidth: 300, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}>
                <span>{config.balanceTitle} used{multi ? ' (at set-off rates)' : ''}</span>
                <span><strong>{matchCcy} {money(balanceUsed)}</strong>{anyForeign && balanceUsed > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right' }}>clears {hc} {money(baseOf(balanceRows))} booked</div>}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}>
                <span>{config.openTitle} cleared{multi ? ' (at set-off rates)' : ''}</span>
                <span><strong>{matchCcy} {money(openUsed)}</strong>{anyForeign && openUsed > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right' }}>clears {hc} {money(baseOf(openRows))} booked</div>}</span>
              </div>
              {!sidesMatch && (balanceUsed > 0 || openUsed > 0) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: '#dc2626' }}>
                  <span>Still to match</span><span>{matchCcy} {money(Math.abs(round2(balanceUsed - openUsed)))} on the {balanceUsed > openUsed ? openWord : balanceWord} side</span>
                </div>
              )}
              {sidesMatch && anyForeign && fx !== 0 && (
                <div style={{ padding: '0.2rem 0', color: '#d97706' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Exchange {fx > 0 ? 'gain' : 'loss'} ({hc})</span><span>{money(Math.abs(fx))}</span></div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {multi ? 'Matched at the set-off rates, but the items were booked at other rates' : `Same ${matchCcy} on both sides, booked at different rates`}: {openWord} clear {hc} {money(baseOf(openRows))}, {balanceWord} clear {hc} {money(baseOf(balanceRows))}.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={saving || !canPost}><IconSystem.check size={16} /> Post Set-off</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen} title="Post Set-off"
        message={`Set off ${matchCcy} ${money(openUsed)}${multi ? ` (in ${hc} at the set-off rates)` : ''} of this ${config.partyWord.toLowerCase()}'s ${balanceWord} against its ${openWord}${anyForeign && fx !== 0 ? `, with an exchange ${fx > 0 ? 'gain' : 'loss'} of ${hc} ${money(Math.abs(fx))}` : ''}? No money moves; a journal records the matching.`}
        confirmLabel="Post" cancelLabel="Cancel" variant="default" onConfirm={submit} onCancel={() => setConfirmOpen(false)} />
    </>
  );
};

// ---- Mark short-paid / Mark withheld modal (ADR-0060 §6) ----
interface MarkRow { amount: string; reason: string; remarks: string }

interface MarkDeductionProps {
  config: PartySetoffConfig;
  isOpen: boolean;
  initialParty?: CarriedParty | null;
  onPartyChange?: (party: CarriedParty | null) => void;
  modeSwitch?: React.ReactNode;
  onSaved: () => void;
  onClose: () => void;
}
const MarkDeductionModal: React.FC<MarkDeductionProps> = ({ config, isOpen, initialParty, onPartyChange, modeSwitch, onSaved, onClose }) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  const { service } = config;
  const [partyId, setPartyId] = useState('');
  const [partyLabel, setPartyLabel] = useState('');
  const [searchAll, setSearchAll] = useState(false);
  const [cands, setCands] = useState<SetoffCandidatesDto | null>(null);
  const [marks, setMarks] = useState<Record<string, MarkRow>>({});
  const [reasons, setReasons] = useState<SelectOption[]>([]);
  const [markDate, setMarkDate] = useState(today());
  const [narration, setNarration] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const partyLabels = useRef(new Map<string, string>());
  const loadParties = usePartyLoader(config.partyType, searchAll, partyLabels);
  const loadBills = useCallback(async (pid: string) => {
    setCands(null); setMarks({});
    if (!pid) return;
    setLoading(true);
    try { setCands(await service.candidates(pid)); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message || `Could not load the ${config.partyWord.toLowerCase()}'s bills.`); }
    finally { setLoading(false); }
  }, [service, config.partyWord]);

  useEffect(() => {
    if (!isOpen) return;
    setPartyId(''); setPartyLabel(''); setSearchAll(false); setCands(null); setMarks({});
    setMarkDate(defaultDateIn(fyRef.current)); setNarration('');
    service.deductionReasons().then(rs => setReasons(rs.map(r => ({ value: r.value, label: r.label })))).catch(() => setReasons([]));
    if (initialParty?.id) {
      setPartyId(initialParty.id); setPartyLabel(initialParty.label);
      loadBills(initialParty.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, service, loadBills]);

  const hc = cands?.functionalCurrencyCode || '';
  // Only bills the party owes us / we owe them can be marked — never a deduction item itself.
  const billRows = ((config.balanceSide === 'DR' ? cands?.creditItems : cands?.debitItems) || [])
    .filter(i => i.itemKind === 'BILL' && !i.deductionKind);
  const isTicked = (id: string) => !!marks[id];
  const toggle = (i: SetoffCandidateDto, on: boolean) => setMarks(prev => {
    const next = { ...prev };
    if (on) next[i.openItemId] = { amount: String(i.fcBalance), reason: '', remarks: '' };
    else delete next[i.openItemId];
    return next;
  });
  const patch = (id: string, p: Partial<MarkRow>) => setMarks(prev => ({ ...prev, [id]: { ...prev[id], ...p } }));
  const picked = billRows.filter(i => isTicked(i.openItemId));
  const problems: string[] = [];
  picked.forEach(i => {
    const m = marks[i.openItemId];
    const a = parseFloat(m.amount) || 0;
    if (a <= 0) problems.push(`${i.referenceNo}: enter the amount to mark ${config.deductionWord}.`);
    else if (a > i.fcBalance + 0.005) problems.push(`${i.referenceNo}: more than its open ${i.currencyCode || hc} ${money(i.fcBalance)}.`);
    if (!m.reason) problems.push(`${i.referenceNo}: pick a reason.`);
    if (markDate < (i.itemDate || '').slice(0, 10)) problems.push(`The date cannot be before ${i.referenceNo}'s date.`);
  });
  const canPost = picked.length > 0 && problems.length === 0 && !!markDate;
  // Preview in home currency at each bill's booked rate (the server prices the same way).
  const totalBase = round2(picked.reduce((s, i) => {
    const a = Math.min(parseFloat(marks[i.openItemId].amount) || 0, i.fcBalance);
    return s + (Math.abs(a - i.fcBalance) < 0.005 ? i.baseBalance : a * bookedRate(i));
  }, 0));
  const billLeg = config.balanceSide === 'DR' ? 'Dr' : 'Cr';   // AP: Dr the bill; AR: Cr the invoice
  const itemLeg = billLeg === 'Dr' ? 'Cr' : 'Dr';

  const submit = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const saved = await service.markDeduction({
        partyId, markDate, narration: narration.trim() || null,
        items: picked.map(i => ({
          openItemId: i.openItemId,
          amount: parseFloat(marks[i.openItemId].amount) || 0,
          reasonCode: marks[i.openItemId].reason,
          remarks: marks[i.openItemId].remarks.trim() || null,
        })),
      });
      notificationService.toast.success(`${saved.setoffNumber || 'Mark'} posted — ${picked.length} bill${picked.length > 1 ? 's' : ''} closed; ${saved.currencyCode} ${money(saved.totalAmount)} stays open as ${config.deductionWord}.`);
      onSaved(); onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || `Could not mark the bills ${config.deductionWord}.`);
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {(saving || loading) && <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}><div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}><h2 className="modal-title">New {config.title}</h2>{modeSwitch}</div>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-grid-3">
            <div className="form-group">
              <SyutSelect key={String(searchAll)} label={<>{config.partyWord} <span className="text-danger">*</span></>} isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={partyId ? [{ value: partyId, label: partyLabel }] : undefined}
                value={partyId} onChange={v => {
                  const id = (v as string) || '';
                  const label = partyLabels.current.get(id) || '';
                  setPartyId(id); setPartyLabel(label); loadBills(id);
                  onPartyChange?.(id ? { id, label } : null);
                }}
                menuPortalTarget={document.body} placeholder={searchAll ? 'Search all parties' : `Select ${config.partyWord.toLowerCase()}`} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={searchAll} onChange={e => setSearchAll(e.target.checked)} /> {config.partyWord} not listed? Search all parties
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Date <span className="text-danger">*</span></label>
              <FyDateInput className="form-input" value={markDate} onChange={setMarkDate} />
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.6rem' }}>{config.markHint}</div>

          {cands && (billRows.length > 0 ? (
            <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                  <th style={{ width: 32, padding: '0.4rem' }} />
                  <th style={{ textAlign: 'left', padding: '0.4rem' }}>Bill</th>
                  <th style={{ width: 100, textAlign: 'left', padding: '0.4rem' }}>Date</th>
                  <th style={{ width: 140, textAlign: 'right', padding: '0.4rem' }}>Open</th>
                  <th style={{ width: 130, textAlign: 'right', padding: '0.4rem' }}>Mark {config.deductionWord}</th>
                  <th style={{ width: 170, textAlign: 'left', padding: '0.4rem' }}>Reason</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {billRows.map(i => {
                  const m = marks[i.openItemId];
                  const on = !!m;
                  return (
                    <tr key={i.openItemId} style={{ borderBottom: '1px solid var(--border-color)', ...(on ? DEDUCTION_ROW : {}) }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={on} onChange={e => toggle(i, e.target.checked)} aria-label={`Mark ${i.referenceNo}`} />
                      </td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>
                        {i.referenceNo}
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{itemLabel(i)}{i.businessUnitName ? ` · ${i.businessUnitName}` : ''}</div>
                      </td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{(i.itemDate || '').slice(0, 10)}</td>
                      <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{i.currencyCode || hc} {money(i.fcBalance)}</td>
                      <td style={{ padding: '0.25rem 0.4rem' }}>
                        <input type="number" min={0} step="0.01" className="form-input" disabled={!on}
                          style={{ width: '100%', textAlign: 'right', fontSize: '0.8125rem', padding: '0.3rem 0.45rem' }}
                          value={m?.amount ?? ''} placeholder="—" onChange={e => patch(i.openItemId, { amount: e.target.value })}
                          aria-label={`Amount to mark on ${i.referenceNo}`} />
                      </td>
                      <td style={{ padding: '0.25rem 0.4rem' }}>
                        {on ? (
                          <SyutSelect options={reasons} value={m.reason} onChange={v => patch(i.openItemId, { reason: (v as string) || '' })}
                            menuPortalTarget={document.body} placeholder="Pick a reason" isClearable={false} size="sm" />
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ padding: '0.25rem 0.4rem' }}>
                        <input type="text" className="form-input" maxLength={500} disabled={!on}
                          style={{ fontSize: '0.8rem', padding: '0.3rem 0.45rem' }}
                          value={m?.remarks ?? ''} placeholder="e.g. claim no., GRN, what was agreed"
                          onChange={e => patch(i.openItemId, { remarks: e.target.value })} aria-label={`Remarks for ${i.referenceNo}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>This {config.partyWord.toLowerCase()} has no open bills to mark.</div>
          ))}

          {problems.length > 0 && (
            <div style={{ fontSize: '0.8rem', marginTop: '0.6rem', padding: '0.45rem 0.7rem', borderRadius: 6, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.35)', color: '#dc2626' }}>
              {problems.map((p, i) => <div key={i}>⚠ {p}</div>)}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Narration</label>
              <textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} placeholder="Defaults to the marked bills" />
            </div>
            {picked.length > 0 && (
              <div style={{ minWidth: 300, fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>What this posts (journal, no bank line)</div>
                {picked.map(i => (
                  <div key={i.openItemId} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '0 0.4rem' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{billLeg}</span><span>Against {i.referenceNo}</span>
                    <span style={{ color: 'var(--warning-text, #92400e)' }}>{itemLeg}</span><span style={{ color: 'var(--warning-text, #92400e)' }}>{config.deductionWord === 'withheld' ? 'Withheld' : 'Short-paid'} on {i.referenceNo}</span>
                  </div>
                ))}
                <div style={{ marginTop: '0.3rem', color: 'var(--text-secondary)' }}>Kept open: <strong>{hc} {money(totalBase)}</strong></div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={saving || !canPost}><IconSystem.check size={16} /> Post</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen} title={config.markLabel}
        message={`Close ${picked.length} bill${picked.length > 1 ? 's' : ''} and keep ${hc} ${money(totalBase)} open as ${config.deductionWord} on this ${config.partyWord.toLowerCase()}'s ledger? No money moves.`}
        confirmLabel="Post" cancelLabel="Cancel" variant="default" onConfirm={submit} onCancel={() => setConfirmOpen(false)} />
    </>
  );
};

// ---- View modal ----
const SetoffViewModal: React.FC<{ config: PartySetoffConfig; setoff: SetoffDto | null; onClose: () => void }> = ({ config, setoff, onClose }) => {
  if (!setoff) return null;
  const isMark = setoff.setoffKind === 'MARK_DEDUCTION';
  const side = (s: string) => (s === config.balanceSide ? config.balanceTitle.replace(/s$/, '') : config.openTitle.replace(/s$/, ''));
  // Foreign items carry a booked rate other than 1 — then the base each leg cleared explains any FX.
  const foreignView = setoff.lines.some(l => Math.abs(l.itemRate - 1) > 0.000001);
  const multiView = setoff.lines.some(l => l.setoffRate != null);
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{isMark ? config.markLabel.replace('Mark', 'Marked') : 'Set-off'} {setoff.setoffNumber}</h2><button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button></div>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>{config.partyWord}</div><strong>{setoff.partyName}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div>{(setoff.setoffDate || '').slice(0, 10)}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>{isMark ? `Kept open as ${config.deductionWord}` : `Matched${multiView ? ' (at set-off rates)' : ''}`}</div>{setoff.currencyCode} {money(setoff.totalAmount)}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div><span className="badge">{setoff.docStatus}</span></div>
          </div>
          {setoff.narration && <div style={{ fontSize: '0.82rem', marginBottom: '0.5rem' }}>{setoff.narration}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
              {!isMark && <th style={{ padding: '0.3rem' }}>Side</th>}
              <th style={{ padding: '0.3rem' }}>Item</th><th style={{ padding: '0.3rem' }}>Date</th>
              <th style={{ padding: '0.3rem', textAlign: 'right' }}>{isMark ? 'Marked' : 'Used'}</th>
              {isMark && <th style={{ padding: '0.3rem' }}>Reason</th>}
              {multiView && <th style={{ padding: '0.3rem', textAlign: 'right' }}>Set-off rate</th>}
              {foreignView && <th style={{ padding: '0.3rem', textAlign: 'right' }}>Booked rate</th>}
              {foreignView && <th style={{ padding: '0.3rem', textAlign: 'right' }}>Base cleared</th>}
            </tr></thead>
            <tbody>{setoff.lines.map(l => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                {!isMark && <td style={{ padding: '0.3rem' }}>{side(l.itemSide)}</td>}
                <td style={{ padding: '0.3rem' }}>{CLASS_LABEL[l.sourceVoucherClass || ''] || l.itemKind} {l.sourceTransactionNo || ''}<span style={{ color: 'var(--text-muted)' }}>{l.referenceNo ? ` · ${l.referenceNo}` : ''}</span></td>
                <td style={{ padding: '0.3rem' }}>{(l.itemDate || '').slice(0, 10)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{l.currencyCode || setoff.currencyCode} {money(l.amount)}</td>
                {isMark && <td style={{ padding: '0.3rem' }}>{l.reasonLabel || l.reasonCode || '—'}{l.remarks && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{l.remarks}</div>}</td>}
                {multiView && <td style={{ padding: '0.3rem', textAlign: 'right' }}>{l.setoffRate != null ? l.setoffRate.toFixed(4) : '—'}</td>}
                {foreignView && <td style={{ padding: '0.3rem', textAlign: 'right' }}>{l.itemRate.toFixed(4)}</td>}
                {foreignView && <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.baseAmount)}</td>}
              </tr>
            ))}</tbody>
          </table>
          {setoff.fxAmount !== 0 && <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: '#d97706' }}>Exchange {setoff.fxAmount > 0 ? 'gain' : 'loss'}: {money(Math.abs(setoff.fxAmount))} (base)</div>}
          {isMark && <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>The bills closed; each amount stays open on the {config.partyWord.toLowerCase()}'s ledger as {config.deductionWord} until it is settled, set off or written off.</div>}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </>
  );
};

// ---- Screen ----
interface PartySetoffScreenProps { config: PartySetoffConfig; moduleIcon?: string; tabId?: string; moduleName?: string; }

const PartySetoffScreen: React.FC<PartySetoffScreenProps> = ({ config, moduleIcon, tabId }) => {
  const can = useCan(config.screenCode);
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const [queryParams, setQueryParams] = useState<SetoffQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = config.useList(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);
  // New Set-off is one window with two modes (ADR-0060): Match items, or Mark short-paid / withheld.
  const [newOpen, setNewOpen] = useState(false);
  const [newMode, setNewMode] = useState<NewSetoffMode>('MATCH');
  /** The party picked in either mode, so switching keeps it. */
  const [carriedParty, setCarriedParty] = useState<CarriedParty | null>(null);
  const [fromDebitNoteId, setFromDebitNoteId] = useState<string | null>(null);
  const openNew = useCallback((mode: NewSetoffMode = 'MATCH') => { setCarriedParty(null); setNewMode(mode); setNewOpen(true); }, []);
  const closeNew = useCallback(() => { setNewOpen(false); setFromDebitNoteId(null); setCarriedParty(null); }, []);
  const switchMode = useCallback((m: NewSetoffMode) => { setFromDebitNoteId(null); setNewMode(m); }, []);
  const [viewSetoff, setViewSetoff] = useState<SetoffDto | null>(null);

  useNavTarget(config.screenCode, (t) => {
    if (t.intent === 'from-debit-note') { openNew('MATCH'); setFromDebitNoteId(t.recordId); return; }
    config.service.getById(t.recordId).then(setViewSetoff).catch(() => notificationService.toast.error('Could not open the set-off.'));
  });

  const handleView = useCallback(async (r: SetoffDto) => {
    try { setViewSetoff(await config.service.getById(r.id)); }
    catch { notificationService.toast.error('Could not load the set-off.'); }
  }, [config.service]);
  const handleCancel = useCallback(async (r: SetoffDto) => {
    if (!(await confirmDelete({ resourceLabel: `${r.setoffKind === 'MARK_DEDUCTION' ? 'mark' : 'set-off'} ${r.setoffNumber || ''}` }))) return;
    try {
      await config.service.cancel(r.id, r.rowVersion);
      notificationService.toast.success(r.setoffKind === 'MARK_DEDUCTION'
        ? `${r.setoffNumber || 'Mark'} cancelled — the bills are open again and the ${config.deductionWord} amounts are removed.`
        : `Set-off ${r.setoffNumber || ''} cancelled — the items are open again.`);
      refetch();
    } catch (err: any) { notificationService.toast.error(err?.response?.data?.message || 'Could not cancel.'); }
  }, [confirmDelete, refetch, config.service, config.deductionWord]);

  const columns: SyutGridColumn<SetoffDto>[] = useMemo(() => [
    { id: 'setoffNumber', header: 'No.', accessorKey: 'setoffNumber', size: 120, cell: ({ row }) => (row.original as SetoffDto).setoffNumber || '—' },
    { id: 'setoffDate', header: 'Date', accessorKey: 'setoffDate', size: 105, cell: ({ row }) => ((row.original as SetoffDto).setoffDate || '').slice(0, 10) },
    {
      id: 'setoffKind', header: 'Kind', accessorKey: 'setoffKind', size: 140,
      cell: ({ row }) => (row.original as SetoffDto).setoffKind === 'MARK_DEDUCTION'
        ? <span className="badge" style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}>Marked {config.deductionWord}</span>
        : <span className="badge">Set-off</span>,
    },
    { id: 'partyName', header: config.partyWord, accessorKey: 'partyName', size: 240 },
    { id: 'totalAmount', header: 'Amount', accessorKey: 'totalAmount', size: 150, align: 'right', cell: ({ row }) => { const r = row.original as SetoffDto; return <span>{r.currencyCode} {money(r.totalAmount)}</span>; } },
    { id: 'fxAmount', header: 'FX (base)', accessorKey: 'fxAmount', size: 110, align: 'right', cell: ({ row }) => { const f = (row.original as SetoffDto).fxAmount; return f ? <span style={{ color: f > 0 ? '#16a34a' : '#dc2626' }}>{money(f)}</span> : '—'; } },
    { id: 'docStatus', header: 'Status', accessorKey: 'docStatus', size: 110, cell: ({ row }) => <span className="badge">{(row.original as SetoffDto).docStatus}</span> },
  ], [config.partyWord, config.deductionWord]);
  // The row-actions column is structural: always visible, whatever columns are shown.
  const visibleColumns = useMemo(() => [...STRUCTURAL_COLUMN_IDS, ...columns.map(c => String(c.id))], [columns]);

  const rowActions = useCallback((r: SetoffDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) }];
    if (r.docStatus === 'POSTED' && can('DELETE')) items.push({ id: 'cancel', label: r.setoffKind === 'MARK_DEDUCTION' ? 'Cancel Mark' : 'Cancel Set-off', icon: <IconSystem.trash size={18} />, onClick: () => handleCancel(r), variant: 'danger' });
    return items;
  }, [can, handleView, handleCancel]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(() => { setFromDebitNoteId(null); openNew('MATCH'); }, 'New Set-off'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];
  const modeSwitch = <ModeSwitch config={config} mode={newMode} onSwitch={switchMode} />;

  return (
    <EnterpriseScreenShell
      title={config.title}
      description={config.description}
      icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: `Search number / ${config.partyWord.toLowerCase()} / reference...` }}
      ConfirmDialogPortal={ConfirmDialogPortal}
    >
      <div className="data-grid-container">
        <SyutGrid data={data} columns={columns} visibleColumns={visibleColumns}
          frozenRightColumns={['actions']}
          enablePagination manualPagination totalCount={pagination.totalItems}
          currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
          onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
          onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
          rowActions={rowActions}
          emptyMessage={loading ? 'Loading...' : `Nothing yet — click New Set-off to match items or ${config.markLabel.toLowerCase()}.`} />
      </div>
      <SetoffFormModal config={config} isOpen={newOpen && newMode === 'MATCH'} fromDebitNoteId={fromDebitNoteId}
        initialParty={carriedParty} onPartyChange={setCarriedParty} modeSwitch={modeSwitch}
        onSaved={refetch} onClose={closeNew} />
      <MarkDeductionModal config={config} isOpen={newOpen && newMode === 'MARK'}
        initialParty={carriedParty} onPartyChange={setCarriedParty} modeSwitch={modeSwitch}
        onSaved={refetch} onClose={closeNew} />
      <SetoffViewModal config={config} setoff={viewSetoff} onClose={() => setViewSetoff(null)} />
    </EnterpriseScreenShell>
  );
};

export default PartySetoffScreen;
